import { useMutation, useQuery } from '@tanstack/react-query';
import { FlowDefinitionState } from 'flow-schema';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { api, getQueryProps, isDefined, isTruthy, useToast } from 'common';
import { type Flow } from 'vivaa-api';

const { FlowDefinitionStateProps } = api.FlowDefinition;

export function useFlow() {
  const { flowId } = useParams<{ flowId: string }>();
  const flowResponse = useQuery({
    ...getQueryProps(api.client.Flow, 'findOneById', flowId),
    enabled: isTruthy(flowId),
  });
  const toast = useToast();
  const flowMutation = useMutation({
    mutationFn: async (values: Flow.UpdateValues) => {
      if (!isDefined(flowId)) {
        throw new Error('Flow ID is undefined');
      }
      return await api.client.Flow.update(flowId, values);
    },
    onError: () => {
      toast.showError('Failed to update.');
    },
  });

  const { data } = flowResponse;
  const { flow, flowDefinitions } = data ?? {};

  const descendingFlowDefinitionsByVersion = flowDefinitions?.sort((a, b) => b.version - a.version);
  const latestPublishedFlowDefinition = descendingFlowDefinitionsByVersion?.find(
    (flowDefinition) => flowDefinition.state === FlowDefinitionState.Published
  );
  const latestVisibleFlowDefinition =
    latestPublishedFlowDefinition ?? descendingFlowDefinitionsByVersion?.[0];
  const latestUnpublishedFlowDefinition = descendingFlowDefinitionsByVersion?.find(
    (flowDefinition) => FlowDefinitionStateProps[flowDefinition.state].isPrePublishState
  );
  const visibleFlowDefinitions = [
    latestPublishedFlowDefinition,
    latestUnpublishedFlowDefinition,
  ].filter(isDefined);

  const flowVersions = isDefined(descendingFlowDefinitionsByVersion)
    ? Array.from(
        {
          length: descendingFlowDefinitionsByVersion.at(0)?.version ?? 0,
        },
        (_, i) => (i + 1).toString()
      )
    : [];

  const flowDefinitionsById = useMemo(() => {
    if (!isDefined(flowDefinitions)) {
      return {};
    }
    return keyBy(flowDefinitions, 'id');
  }, [flowDefinitions]);

  return {
    flowId,
    flowResponse,
    flow,
    flowDefinitions,
    flowMutation,
    descendingFlowDefinitionsByVersion,
    latestPublishedFlowDefinition,
    latestVisibleFlowDefinition,
    latestUnpublishedFlowDefinition,
    visibleFlowDefinitions,
    flowVersions,
    flowDefinitionsById,
  };
}
